import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/cn";

interface BrokenMediaProps {
  className?: string;
  kind?: "img" | "video";
}

/**
 * Visibly-broken media placeholder (UI Regression Defense, Layer 4 — REF-6063).
 *
 * Media that fails to load must LOOK broken, never blank: the old white fallback
 * PNGs (`image-fallback.png`, `img-default.png`) turned every load failure into an
 * innocent white tile, which is how a dead video thumbnail survived 4 months
 * unreported. This tile is deliberately unmistakable in both themes.
 *
 * Inline SVG on purpose — no binary asset to keep in sync per theme, scales to any
 * cell size, and it can never itself fail to load. Iconographic only (no visible
 * text), so the only i18n surface is the aria-label. Colors are explicit
 * light+dark pairs; never a bare `text-primary` (Trap #12 — mint vanishes on white).
 */
const BrokenMedia: React.FC<BrokenMediaProps> = ({ className, kind = "img" }) => {
  const { t } = useTranslation();

  return (
    <div
      data-testid="broken-media"
      role="img"
      aria-label={t("common:media.load_failed")}
      // bg pair per PhotoshootsItem precedent; `grey` is itself theme-aware
      // (#6E6E6E light / #939393 dark) — ≥4.2:1 on both tile fills. There is no
      // black-400 token despite stray `*-black-400` classes elsewhere in the repo.
      className={cn("w-full h-full flex items-center justify-center bg-black-200 dark:bg-black-300 text-grey", className)}
    >
      {kind === "video" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-1/3 h-1/3 max-w-8 max-h-8 min-w-4 min-h-4">
          <rect x="2" y="5" width="14" height="14" rx="2" />
          <path d="M16 10.5l6-3.5v10l-6-3.5" />
          <path d="M3 3l18 18" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-1/3 h-1/3 max-w-8 max-h-8 min-w-4 min-h-4">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="1.5" />
          <path d="M21 15l-4.5-4.5L9 18" />
          <path d="M3 3l18 18" />
        </svg>
      )}
    </div>
  );
};

export default BrokenMedia;
